// Drill-down for one user — profile, subs, progress, recent recordings,
// Zoho timeline.

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getUser, patchUserAccessOverride } from '../adminApi';
import { Loading, Err } from './Overview';
import { shortDate } from './Users';

export default function UserDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [accessOverride, setAccessOverride] = useState(false);
    const [overrideSaving, setOverrideSaving] = useState(false);
    const [overrideTick, setOverrideTick] = useState(false);
    const [overrideErr, setOverrideErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setData(null);
            try {
                const d = await getUser(id);
                if (!cancelled) {
                    setData(d);
                    setAccessOverride(d?.user?.admin_access_override || false);
                }
            } catch (e) {
                if (!cancelled) setErr(e?.message || 'Failed to load');
            }
        })();
        return () => { cancelled = true; };
    }, [id]);

    const onToggleOverride = async (e) => {
        const next = e.target.checked;
        const prev = accessOverride;
        setAccessOverride(next);
        setOverrideErr(null);
        setOverrideSaving(true);
        try {
            await patchUserAccessOverride(id, next);
            setOverrideTick(true);
            setTimeout(() => setOverrideTick(false), 1400);
        } catch (ex) {
            setAccessOverride(prev);
            setOverrideErr(ex?.response?.data?.error || ex?.message || 'Failed to save');
        } finally {
            setOverrideSaving(false);
        }
    };

    if (err) return <Err msg={err} />;
    if (!data) return <Loading />;

    const u = data.user || {};

    return (
        <div>
            <Link to="/admin/users" style={styles.back}>← Users</Link>
            <h2 style={styles.h2}>{u.email}</h2>
            <div style={styles.profile}>
                <Field label="Name" value={[u.fname, u.lname].filter(Boolean).join(' ') || '—'} />
                <Field label="Signed up" value={shortDate(u.created_at)} />
                <Field label="Last login" value={shortDate(u.last_login_at)} />
                <Field label="Confirmed" value={u.confirmed ? 'Yes' : 'No'} />
                <Field label="Roles" value={(u.roles || []).join(', ') || '—'} />
                <Field label="Subscription" value={u.subscription_status} />
                <Field label="Convos completed" value={u.n_conversations_completed} />
                <Field label="Lines spoken" value={u.n_lines_spoken} />
                <Field label="Avg Capeesh" value={u.av_capeesh_score ?? '—'} />
                <div style={styles.field}>
                    <div style={styles.fieldLabel}>Admin access override</div>
                    <label style={styles.toggleLabel}>
                        <input
                            type="checkbox"
                            checked={accessOverride}
                            onChange={onToggleOverride}
                            disabled={overrideSaving}
                            style={{ marginRight: 6 }}
                        />
                        {accessOverride ? 'Granted' : 'Not granted'}
                        {overrideTick && <span style={styles.tick}> ✓</span>}
                    </label>
                    {overrideErr && <div style={styles.overrideErr}>{overrideErr}</div>}
                </div>
            </div>

            <Section title="Subscriptions">
                {data.subscriptions.length === 0 ? (
                    <Empty msg="No subscriptions on record." />
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <Th>Type</Th><Th>Purchased</Th><Th>Last renewed</Th>
                                <Th>Next renew</Th><Th>Expires</Th><Th>Cancelled</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.subscriptions.map((s) => (
                                <tr key={s.id}>
                                    <Td>{s.subscription_type_title || s.subscription_type_id || '—'}</Td>
                                    <Td>{shortDate(s.date_purchased)}</Td>
                                    <Td>{shortDate(s.date_last_renewed)}</Td>
                                    <Td>{shortDate(s.next_renew_at)}</Td>
                                    <Td>{shortDate(s.date_expires)}</Td>
                                    <Td>{s.canceled ? 'Yes' : 'No'}</Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Section>

            <Section title="Recent recordings">
                {data.recordings.length === 0 ? (
                    <Empty msg="No recordings yet." />
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr><Th>When</Th><Th>Line</Th><Th align="right">Capeesh</Th></tr>
                        </thead>
                        <tbody>
                            {data.recordings.map((r) => (
                                <tr key={r.id}>
                                    <Td>{shortDate(r.created_at)}</Td>
                                    <Td style={{ color: '#444' }}>{r.line_text || `Line #${r.line_id}`}</Td>
                                    <Td align="right">{r.capeesh_score ?? '—'}</Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Section>

            <Section title="Conversation progress">
                {data.progress.length === 0 ? (
                    <Empty msg="Nothing started yet." />
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr><Th>Conversation</Th><Th>Chapter/Lecture</Th><Th>Stage</Th><Th align="right">Progress</Th></tr>
                        </thead>
                        <tbody>
                            {data.progress.map((p) => (
                                <tr key={p.id}>
                                    <Td>{p.conversation_title || `#${p.conversation_id}`}</Td>
                                    <Td>{p.chapter}/{p.lecture}</Td>
                                    <Td>{p.current_stage || '—'}</Td>
                                    <Td align="right">{p.progress ?? 0}%</Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Section>

            <Section title="Practice">
                <div style={{ color: '#666', fontSize: 14 }}>
                    Total practice reviews:{' '}
                    <strong style={{ color: '#222' }}>{data.practice_count.toLocaleString()}</strong>
                </div>
            </Section>

            <Section title="Zoho sync log">
                {data.zoho_log.length === 0 ? (
                    <Empty msg="No Zoho events recorded for this user." />
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr><Th>When</Th><Th>List</Th><Th>Action</Th><Th>Status</Th></tr>
                        </thead>
                        <tbody>
                            {data.zoho_log.map((z) => (
                                <tr key={z.id}>
                                    <Td>{shortDate(z.created_at)}</Td>
                                    <Td>{z.list_name || '—'}</Td>
                                    <Td>{z.action}</Td>
                                    <Td>
                                        <span style={{ color: z.status === 'ok' ? '#2e7d32' : '#c0392b', fontWeight: 600 }}>
                                            {z.status}
                                        </span>
                                    </Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Section>
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div style={styles.field}>
            <div style={styles.fieldLabel}>{label}</div>
            <div style={styles.fieldValue}>{value}</div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div style={styles.section}>
            <div style={styles.sectionTitle}>{title}</div>
            {children}
        </div>
    );
}

function Empty({ msg }) {
    return <div style={{ color: '#999', fontSize: 13, fontStyle: 'italic' }}>{msg}</div>;
}

function Th({ children, align }) {
    return <th style={{ ...styles.th, textAlign: align || 'left' }}>{children}</th>;
}
function Td({ children, align, style }) {
    return <td style={{ ...styles.td, textAlign: align || 'left', ...(style || {}) }}>{children}</td>;
}

const styles = {
    back: { color: '#888', textDecoration: 'none', fontSize: 13 },
    h2: { color: '#222', marginTop: 8 },
    profile: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: 12,
        backgroundColor: '#fff',
        padding: 16,
        borderRadius: 12,
        marginBottom: 18,
    },
    field: {},
    fieldLabel: { fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 },
    fieldValue: { fontSize: 15, color: '#222', fontWeight: 600, marginTop: 2 },
    toggleLabel: { fontSize: 14, color: '#222', fontWeight: 600, marginTop: 2, display: 'flex', alignItems: 'center', cursor: 'pointer' },
    tick: { color: '#2e7d32', marginLeft: 4 },
    overrideErr: { fontSize: 12, color: '#c0392b', marginTop: 4 },
    section: { marginBottom: 22, backgroundColor: '#fff', padding: 16, borderRadius: 12 },
    sectionTitle: { fontSize: 13, color: '#d97447', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { padding: '8px 12px', borderBottom: '1px solid #eee', color: '#888', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 },
    td: { padding: '8px 12px', borderBottom: '1px solid #f3f3f3', color: '#222' },
};
